import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Foooter';
import PageHero from '../Components/PageHero';
import PersistentCTA from '../Components/CRO/PersistentCTA';
import StickyMobileCTA from '../Components/CRO/StickyMobileCTA';
import { getServiceBySlug } from '../data/servicesData';
import { motion } from 'framer-motion';
import {
  FaSearch,
  FaGoogle,
  FaFacebookF,
  FaYoutube,
  FaSms,
  FaEnvelope,
  FaFileAlt,
  FaWhatsapp,
  FaShoppingCart,
  FaPaintBrush,
  FaHashtag,
  FaCode,
  FaCheckCircle,
  FaArrowRight,
  FaPhoneAlt,
} from 'react-icons/fa';
import ServiceSchema from '../Components/SEO/ServiceSchema';

const iconMap = {
  FaSearch,
  FaGoogle,
  FaFacebookF,
  FaYoutube,
  FaSms,
  FaEnvelope,
  FaFileAlt,
  FaWhatsapp,
  FaShoppingCart,
  FaPaintBrush,
  FaHashtag,
  FaCode,
};

const ServiceDetail = () => {
  const { serviceName } = useParams();
  const service = getServiceBySlug(serviceName);

  if (!service) {
    return (
      <div>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-light text-gray-900 mb-4 tracking-tight">
              Service <span className="font-semibold">Not Found</span>
            </h1>
            <p className="text-xl font-light text-gray-600 mb-8">The service you're looking for doesn't exist.</p>
            <Link
              to="/services"
              className="inline-block bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 text-white px-8 py-3 rounded-full font-medium hover:opacity-90 transition-opacity shadow-lg"
            >
              View All Services
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ServiceSchema service={service} />
      <Navbar />
      <PageHero 
        title={service.title}
        subtitle={service.shortDesc}
      />

      {/* Problem & Solution Section */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-white min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-lg"
            >
              <h2 className="text-3xl sm:text-4xl font-light text-gray-900 mb-6 tracking-tight">
                The <span className="font-semibold">Challenge</span>
              </h2>
              <p className="text-xl font-light text-gray-600 leading-relaxed">
                {service.problem}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-lg"
            >
              <h2 className="text-3xl sm:text-4xl font-light text-gray-900 mb-6 tracking-tight">
                Our <span className="font-semibold">Solution</span>
              </h2>
              <p className="text-xl font-light text-gray-600 leading-relaxed">
                {service.solution}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-white min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-4 tracking-tight">
              What's <span className="font-semibold">Included</span>
            </h2>
            <p className="text-xl font-light text-gray-600 max-w-2xl mx-auto">
              Comprehensive services designed to deliver measurable results
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-orange-100 to-pink-100 p-2 rounded-lg flex-shrink-0">
                    <FaCheckCircle className="text-orange-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:bg-gradient-to-r group-hover:from-orange-400 group-hover:via-pink-400 group-hover:to-purple-400 group-hover:bg-clip-text group-hover:text-transparent transition-all">
                      {feature}
                    </h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-6 tracking-tight">
              <span className="font-semibold">Proven</span> Results
            </h2>
            <p className="text-xl md:text-2xl font-light text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
              {service.results}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-full font-medium hover:bg-gray-100 transition shadow-lg"
              >
                Get Started Today
                <FaArrowRight />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industry Focus Section */}
      {service.industryFocus && service.industryFocus.length > 0 && (
        <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-white min-h-screen flex items-center">
          <div className="max-w-7xl mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-4 tracking-tight">
                Industry <span className="font-semibold">Expertise</span>
              </h2>
              <p className="text-xl font-light text-gray-600">
                We specialize in delivering results for these industries
              </p>
            </motion.div>
            <div className="flex flex-wrap justify-center gap-4">
              {service.industryFocus.map((industry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="bg-white border border-gray-200 px-6 py-3 rounded-full text-gray-700 font-medium hover:bg-gradient-to-r hover:from-orange-300 hover:via-pink-300 hover:to-purple-300 hover:text-white hover:border-transparent transition-all shadow-sm hover:shadow-lg"
                >
                  {industry}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-white min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-6 tracking-tight">
              Ready to <span className="font-semibold">Get Started?</span>
            </h2>
            <p className="text-xl font-light text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
              Let's discuss how our {service.title} services can transform your business
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 text-white px-8 py-4 rounded-full font-medium hover:opacity-90 transition-opacity shadow-lg"
              >
                Schedule a Consultation
                <FaArrowRight />
              </Link>
              <a
                href="tel:+919164752256"
                className="inline-flex items-center gap-2 border-2 border-gray-200 text-gray-700 px-8 py-4 rounded-full font-medium hover:border-orange-300 hover:text-orange-400 transition-all"
              >
                <FaPhoneAlt />
                Call Us: +91 9164752256
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <PersistentCTA />
      <StickyMobileCTA />
    </>
  );
};

export default ServiceDetail;

